import React, { useState, useEffect } from 'react';
import { plans } from '../api';

const AuthPage = ({ handleLogin, handleRegister, setCurrentPage }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    plan: null, // Initialize as null until plans are loaded
    agreedToTerms: false,
    checkout_session_id: null // Add field for Stripe checkout session ID
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [availablePlans, setAvailablePlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [checkoutReady, setCheckoutReady] = useState(false);

  // Check for messages in localStorage
  useEffect(() => {
    // Check for message in localStorage
    const message = localStorage.getItem('auth_message');
    if (message) {
      setSuccess(message);
      // Clear the message after showing it
      localStorage.removeItem('auth_message');
    }
    
    // Still check URL params as a fallback
    const urlParams = new URLSearchParams(window.location.search);
    const urlMessage = urlParams.get('message');
    
    if (urlMessage === 'account_deleted' && !message) {
      setSuccess('Your account has been successfully deleted. We hope to see you again soon!');
    }
    
    // Check if there's a checkout session ID in localStorage (from payment success)
    const checkoutSessionId = localStorage.getItem('checkout_session_id');
    const selectedPlanId = localStorage.getItem('selected_plan_id');
    
    if (checkoutSessionId && selectedPlanId) {
      // We're returning from a successful payment
      setIsLogin(false); // Switch to registration form
      setCheckoutReady(true);
      setFormData(prev => ({
        ...prev,
        plan: selectedPlanId,
        checkout_session_id: checkoutSessionId
      }));
      
      // Set success message
      setSuccess('Payment successful! Please complete your registration.');
    }
  }, []);

  // Fetch available plans on component mount
  useEffect(() => {
    if (!isLogin) {
      setPlansLoading(true);
      fetchPlans();
    }
  }, [isLogin]);

  // Fetch available subscription plans
  const fetchPlans = async () => {
    try {
      const response = await plans.getPlans();
      if (response.data && response.data.length > 0) {
        setAvailablePlans(response.data);
        
        // Set the first available plan as default
        setFormData(prev => ({
          ...prev,
          plan: response.data[0].id
        }));
        
        // Set the selected plan details
        setSelectedPlan(response.data[0]);
      } else {
        // Handle case when no plans are returned
        console.error('No plans available from API');
        setError('Unable to load subscription plans. Please try again later.');
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError('Unable to load subscription plans. Please try again later.');
    } finally {
      setPlansLoading(false);
    }
  };

  // Email validation function using regex
  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If changing plan, update the selected plan details
    if (name === 'plan') {
      const planObj = availablePlans.find(p => p.id === parseInt(value));
      setSelectedPlan(planObj);
      
      // Reset checkout_session_id if plan changes
      if (formData.plan !== value) {
        setFormData({ 
          ...formData, 
          [name]: value,
          checkout_session_id: null
        });
        setCheckoutReady(false);
      } else {
        setFormData({ ...formData, [name]: value });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
    
    // Clear validation errors when field is changed
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null
      });
    }
  };

  // Validate the form before submission
  const validateForm = () => {
    const errors = {};
    
    // Validate email
    if (!isValidEmail(formData.email)) {
      errors.email = "Please enter a valid email address (e.g., user@example.com)";
    }
    
    // Validate password length if registering
    if (!isLogin && formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters long";
    }
    
    // Validate name if registering
    if (!isLogin && !formData.name.trim()) {
      errors.name = "Name is required";
    }
    
    // Validate plan selection if registering
    if (!isLogin && (!formData.plan)) {
      errors.plan = "Please select a subscription plan";
    }
    
    // Validate payment for paid plans
    if (!isLogin && selectedPlan && selectedPlan.price > 0 && !formData.checkout_session_id && !checkoutReady) {
      errors.payment = "You must complete payment for this plan first";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Stripe checkout for registration with paid plans
  const handleCheckout = async () => {
    if (!selectedPlan) {
      setError('Please select a plan first');
      return;
    }
    
    // Validate user info before checkout
    if (!isValidEmail(formData.email) || !formData.name.trim() || formData.password.length < 8) {
      setError('Please fill out all required fields correctly before proceeding to payment');
      validateForm();
      return;
    }
    
    try {
      setLoading(true);
      
      // Create checkout session
      const response = await plans.createCheckoutSession(selectedPlan.id, false, formData.email);
      
      // For free plans, no redirect is needed
      if (response.data.free_plan) {
        setCheckoutReady(true);
        setLoading(false);
        setSuccess('Free plan selected - continue with registration');
        return;
      }
      
      // Store form data temporarily in sessionStorage
      sessionStorage.setItem('registration_data', JSON.stringify({
        name: formData.name,
        email: formData.email,
        plan: formData.plan,
        agreedToTerms: formData.agreedToTerms
      }));
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setError('Failed to initialize payment. Please try again later.');
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form first
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isLogin) {
        // Login
        const result = await handleLogin(formData.email, formData.password);
        if (!result.success) {
          setError(result.message);
        } else {
          // Check if there was a redirect path stored
          const redirectPath = localStorage.getItem('auth_redirect');
          if (redirectPath) {
            localStorage.removeItem('auth_redirect');
            // Use window.location to navigate to the stored path
            if (redirectPath.startsWith('/')) {
              window.location.pathname = redirectPath;
            } else {
              window.location.hash = `#/${redirectPath}`;
            }
          }
        }
      } else {
        // Register
        // Check if user has agreed to terms
        if (!formData.agreedToTerms) {
          setError('You must agree to the Terms of Service, Privacy Policy, and User Agreement to register.');
          setLoading(false);
          return;
        }
        
        // For paid plans, check if payment was completed
        if (selectedPlan && selectedPlan.price > 0 && !formData.checkout_session_id) {
          setError('Payment required for this plan');
          setLoading(false);
          return;
        }
        
        const userData = {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          plan_id: parseInt(formData.plan),
          terms_accepted: formData.agreedToTerms,
          checkout_session_id: formData.checkout_session_id
        };
        
        const result = await handleRegister(userData);
        
        if (result.success || (result.message && result.message.includes('not verified'))) {
          // Show success message
          setSuccess('Registration successful!');
          setRegistrationComplete(true);
          
          // Clear the checkout session ID and selected plan from localStorage
          localStorage.removeItem('checkout_session_id');
          localStorage.removeItem('selected_plan_id');
        } else {
          // Show actual error
          setError(result.message || 'Registration failed. Please try again or use a different email.');
        }
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Determine if the registration button should show "Pay & Register" or just "Register"
  const getRegisterButtonText = () => {
    if (isLogin) return 'Sign In';
    
    if (!selectedPlan) return 'Register';
    
    if (selectedPlan.price > 0 && !checkoutReady && !formData.checkout_session_id) {
      return `Pay $${selectedPlan.price} & Register`;
    }
    
    return 'Complete Registration';
  };

  // Handle the register button action depending on payment status
  const handleRegisterAction = (e) => {
    e.preventDefault();
    
    if (isLogin) {
      handleSubmit(e);
      return;
    }
    
    // For free plans or if checkout is already complete, proceed with registration
    if ((selectedPlan && selectedPlan.price === 0) || checkoutReady || formData.checkout_session_id) {
      handleSubmit(e);
      return;
    }
    
    // For paid plans without checkout, initiate checkout
    handleCheckout();
  };

  return (
    <div className="flex justify-center items-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-md">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
            {isLogin ? 'Sign in to your account' : 'Create your account'}
          </h2>
          <p className="text-gray-600">
            {isLogin ? 'Welcome back to MenuAnalyzer.AI' : 'Start your journey to healthier eating'}
          </p>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {success}
          </div>
        )}
        
        {registrationComplete ? (
          <div className="text-center">
            <p className="text-lg text-gray-700 mb-4">
              Thank you for registering! Please check your email to verify your account.
            </p>
            <button
              onClick={() => setIsLogin(true)}
              className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none"
            >
              Return to Login
            </button>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleRegisterAction}>
            <div className="space-y-4">
              {!isLogin && (
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <div className="mt-1">
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className={`appearance-none relative block w-full px-3 py-2 border ${
                        validationErrors.name ? 'border-red-300' : 'border-gray-300'
                      } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm`}
                      placeholder="John Doe"
                    />
                    {validationErrors.name && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>
                    )}
                  </div>
                </div>
              )}
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className={`appearance-none relative block w-full px-3 py-2 border ${
                      validationErrors.email ? 'border-red-300' : 'border-gray-300'
                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm`}
                    placeholder="email@example.com"
                  />
                  {validationErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.email}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete={isLogin ? "current-password" : "new-password"}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className={`appearance-none relative block w-full px-3 py-2 border ${
                      validationErrors.password ? 'border-red-300' : 'border-gray-300'
                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm`}
                    placeholder={isLogin ? "Your password" : "Minimum 8 characters"}
                  />
                  {validationErrors.password && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.password}</p>
                  )}
                </div>
              </div>
              
              {!isLogin && (
                <div className="pt-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Subscription Plans</h3>
                  {validationErrors.plan && (
                    <p className="mb-2 text-sm text-red-600">{validationErrors.plan}</p>
                  )}
                  {validationErrors.payment && (
                    <p className="mb-2 text-sm text-red-600">{validationErrors.payment}</p>
                  )}
                  {plansLoading ? (
                    <div className="text-gray-500">Loading plans...</div>
                  ) : (
                    <div className="space-y-2">
                      {availablePlans.map((plan) => (
                        <div 
                          key={plan.id}
                          className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                            parseInt(formData.plan) === plan.id 
                              ? 'border-emerald-500 bg-emerald-50' 
                              : 'border-gray-300 hover:border-emerald-500'
                          } ${(checkoutReady || formData.checkout_session_id) ? 'opacity-75 pointer-events-none' : ''}`}
                          onClick={() => {
                            if (!checkoutReady && !formData.checkout_session_id) {
                              setFormData({ 
                                ...formData, 
                                plan: plan.id.toString(),
                                checkout_session_id: null
                              });
                              setSelectedPlan(plan);
                              setCheckoutReady(false);
                            }
                          }}
                        >
                          <input
                            id={`plan-${plan.id}`}
                            name="plan"
                            type="radio"
                            value={plan.id}
                            checked={parseInt(formData.plan) === plan.id}
                            onChange={handleChange}
                            className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300"
                            disabled={checkoutReady || formData.checkout_session_id}
                          />
                          <label htmlFor={`plan-${plan.id}`} className="ml-3 flex flex-col cursor-pointer w-full">
                            <span className="block text-sm font-medium text-gray-900">{plan.name}</span>
                            <span className="block text-sm text-gray-500">{plan.description}</span>
                            <span className="block text-xs text-gray-500 mt-1">
                              {plan.credits_per_month === -1 ? 'Unlimited credits' : `${plan.credits_per_month} credits/month`}
                            </span>
                          </label>
                          <span className="text-gray-900 font-medium">${plan.price}{plan.price > 0 ? '/mo' : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Payment confirmation indicator */}
                  {formData.checkout_session_id && (
                    <div className="mt-2 text-sm text-green-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                      </svg>
                      Payment completed for {availablePlans.find(p => p.id === parseInt(formData.plan))?.name}
                    </div>
                  )}
                </div>
              )}
              
              {!isLogin && (
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="termsAgreement"
                      name="agreedToTerms"
                      type="checkbox"
                      checked={formData.agreedToTerms}
                      onChange={(e) => setFormData({ ...formData, agreedToTerms: e.target.checked })}
                      className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="termsAgreement" className="font-medium text-gray-700">
                      I agree to the{' '}
                      <a href="/?page=legal&type=terms" className="text-emerald-600 hover:text-emerald-500" target="_blank" rel="noopener noreferrer">
                        Terms of Service
                      </a>,{' '}
                      <a href="/?page=legal&type=privacy" className="text-emerald-600 hover:text-emerald-500" target="_blank" rel="noopener noreferrer">
                        Privacy Policy
                      </a>, and{' '}
                      <a href="/?page=legal&type=user-agreement" className="text-emerald-600 hover:text-emerald-500" target="_blank" rel="noopener noreferrer">
                        User Agreement
                      </a>
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div>
              {isLogin && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                    />
                    <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                      Remember me
                    </label>
                  </div>
                  
                  <div className="text-sm">
                    <button 
                      type="button"
                      onClick={() => window.location.href = '#forgot-password'}
                      className="font-medium text-emerald-600 hover:text-emerald-500"
                    >
                      Forgot your password?
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  loading ? 'bg-emerald-400' : 'bg-emerald-600 hover:bg-emerald-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 relative`}
              >
                {loading ? (
                  <>
                    <span className="absolute left-1/2 transform -translate-x-1/2">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </span>
                    <span className="opacity-0">Processing...</span>
                  </>
                ) : (
                  getRegisterButtonText()
                )}
              </button>
            </div>

            <div className="flex items-center justify-center">
              <div className="text-sm">
                {isLogin ? (
                  <>
                    Don't have an account?{' '}
                    <button
                      type="button"
                      onClick={() => setIsLogin(false)}
                      className="font-medium text-emerald-600 hover:text-emerald-500 focus:outline-none"
                    >
                      Register now
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={() => setIsLogin(true)}
                      className="font-medium text-emerald-600 hover:text-emerald-500 focus:outline-none"
                    >
                      Sign in
                    </button>
                  </>
                )}
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AuthPage;
